import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, signal } from '@angular/core';
import { ConfigService } from '@assist/shared/data';
import { ApiService } from '@cue/api';
import { rxResource } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { NavigationService } from '@cue/navigation-services';
import {
  DeclarativeMapCanvasComponent,
  DeclarativeMapImageComponent,
  DeclarativeMapMarkerComponent,
  DeclarativeMapMarkerTemplateDirective,
} from '@cue/map/elements';
import { DECLARATIVE_MAP_IMPLEMENTATION, provideDeclarativeMapImplementation } from '@cue/map/core';
import { Area, getNextQuarter, StartTargetSelectionModeEnum } from '@cue/data';

import { translation } from '@cue/translations';
import { TranslocoPipe } from '@jsverse/transloco';
import { addMinutes } from 'date-fns';
import {
  FloorplanButtonComponent,
  LeafletStylesComponent,
  MapResourceComponent,
  OpenLayersStylesComponent,
} from '@cue/navigation-widgets';
import { cueArrowRight, cueStart, cueTarget, IconComponent } from '@cue/ui/icons';
import { of } from 'rxjs';
import { LocalizedStringPipe } from '@cue/utilities';
import { ButtonComponent } from '@cue/ui';
import { LoadingComponent } from '@cue/loading';
import { Router } from '@angular/router';
import { DialogRef } from '@angular/cdk/dialog';
import { OpenLayersDeclarativeMapImplementation } from '@cue/map/openlayers';
import { injectQueryParams } from 'ngxtension/inject-query-params';

@Component({
  selector: 'assist-multiview-renew-navigation',
  imports: [
    DeclarativeMapCanvasComponent,
    DeclarativeMapImageComponent,
    ButtonComponent,
    TranslocoPipe,
    MapResourceComponent,
    DeclarativeMapMarkerComponent,
    FloorplanButtonComponent,
    DeclarativeMapMarkerTemplateDirective,
    IconComponent,
    LocalizedStringPipe,
    ButtonComponent,
    LoadingComponent,
    LeafletStylesComponent,
    OpenLayersStylesComponent,
  ],
  templateUrl: './multiview-renew-navigation.component.html',
  styleUrl: './multiview-renew-navigation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideDeclarativeMapImplementation()],
})
export class MultiviewRenewNavigationComponent {
  // Resource NEPRIHLASENY
  // http://localhost:4201/account/login?multiview=f1c44fc7-a94b-4c71-858f-03fb6d79b880
  // RESOURCE PRIHLASENY
  // http://localhost:4201/dashboard?multiview=f1c44fc7-a94b-4c71-858f-03fb6d79b880

  // QR prihlasney
  // http://localhost:4201/dashboard?multiview=78a042a5-36b3-4724-9a4d-efbf3e22c0e6
  // QR neprihlaseny
  // http://localhost:4201/dashboard?multiview=78a042a5-36b3-4724-9a4d-efbf3e22c0e6
  ICONS = {
    cueArrowRight,
    cueStart,
    cueTarget,
  };
  navigationService = inject(NavigationService);
  config = inject(ConfigService);
  apiService = inject(ApiService);
  qrCodeId = input.required<string>();
  areaId = input.required<number>();
  resourceId = input<number>();

  navigationConfiguration = rxResource<any, any>({
    stream: () =>
      this.apiService.call({
        url: '/api/navigation/Configuration',
        method: 'post',
        payload: { kioskId: null },
      }),
  });

  canBeSetAsStart = computed(() => {
    if (!this.navigationDetail.hasValue()) return false;
    return this.navigationDetail.value().setAsStart;
  });

  canBeSetAsTarget = computed(() => {
    if (!this.navigationDetail.hasValue()) return false;
    return this.navigationDetail.value().setAsTarget;
  });

  imageIsSettingUp = signal(true);

  isLoading = computed(() => {
    return (
      this.navigationConfiguration.isLoading() ||
      this.geodataForSelectedArea.isLoading() ||
      this.schedulesForSelectedArea.isLoading() ||
      this.navigationDetail.isLoading()
    );
  });

  areaMapSrc = computed(() => {
    if (this.selectedArea()) {
      return this.selectedArea().floorplan.en;
    }
    return null;
  });

  areaMapHeight = computed(() => {
    if (this.selectedArea()) {
      return this.selectedArea().newFloorplanHeight;
    }
    return 0;
  });

  areaMapWidth = computed(() => {
    if (this.selectedArea()) {
      return this.selectedArea().newFloorplanWidth;
    }
    return 0;
  });

  isAnonymous = input(false);
  selectedArea = computed(() => {
    if (!this.navigationConfiguration.hasValue()) return null;
    const inAreasInfo = this.navigationConfiguration.value().areasInfo.find((x) => x.areaId === this.areaId());
    const inAreasInfoData = this.navigationConfiguration.value().areasInfoData.find((x) => x.id === this.areaId());
    return {
      ...inAreasInfoData,
      ...inAreasInfo,
    };
  });
  zoom = linkedSignal<Area, number>({
    source: this.selectedArea,
    computation: (source, previous) => {
      if (previous?.value === undefined) {
        return source?.zoomLevelDefault ?? 1;
      }
      return previous.value;
    },
  });

  declarativeMapImplementation = inject(DECLARATIVE_MAP_IMPLEMENTATION);
  isOpenLayersMapImplementation = this.declarativeMapImplementation instanceof OpenLayersDeclarativeMapImplementation;

  debug = injectQueryParams('debug', {
    parse: (v) => v === 'true',
  });

  private getSchedulesRequest(areaId: number, kioskId: number | null) {
    let url = '/api/navigation/areas/' + areaId + '/schedules';
    const now = new Date();
    const start = getNextQuarter(now);
    const end = addMinutes(start, 15);
    url += '?start=' + start.toISOString();
    url += '&end=' + end.toISOString();

    if (kioskId) {
      url += '&kioskId=' + kioskId!;
    }

    return this.apiService.call<any, any>({ url: url.toString(), method: 'get' });
  }

  navigationDetail = rxResource({
    params: () => ({
      resourceId: this.resourceId(),
      qrCodeId: this.qrCodeId(),
    }),
    stream: ({ params }) => {
      if (!params.resourceId && !params.qrCodeId) {
        return of(null);
      }
      return this.navigationService
        .getNavigationDetail({
          resourceId: params.resourceId,
          qrCodeId: params.qrCodeId,
          kioskId: null,
          appId: null,
        })
        .pipe(map((r) => r ?? null));
    },
  });

  focus = signal<'qr' | 'resource' | null>(null);

  geodataForSelectedArea = rxResource({
    params: () => this.areaId(),
    stream: ({ params }) =>
      this.navigationService
        .getNavGeodataForAreaId({
          areaId: params,
          deploymentId: null,
          kioskId: null,
          appId: null,
        })
        .pipe(
          map((r) =>
            r.data
              ? r.data.map((x) => ({
                  ...x,
                  geometry: x.geometry,
                }))
              : null
          )
        ),
  });

  qrCodeGeo = computed(() => {
    if (this.isResource() && !this.qrCodeId()) return null;
    return this.geodataForSelectedArea.value()?.find((x) => x.qrCodeId === this.qrCodeId());
  });
  isResource = computed(() => {
    return this.resourceId();
  });

  schedulesForSelectedArea = rxResource({
    params: () => this.areaId(),
    stream: ({ params }) => this.getSchedulesRequest(params, null),
  });

  resourcesForSelectedArea = computed(() => {
    if (!this.geodataForSelectedArea.value()) {
      return [];
    }
    return this.geodataForSelectedArea.value()?.filter((x) => x.typeId === 2);
  });

  resourcesWithSchedulesForSelectedArea = computed(() => {
    const schedules = this.schedulesForSelectedArea.value();
    const resources = this.resourcesForSelectedArea();
    if (!schedules || !resources) {
      return [];
    }
    return resources.map((resource) => {
      const resourceSchedules = schedules.find((s: any) => s.resourceId === resource.resourceId);
      return {
        ...resource,
        schedules: resourceSchedules,
      };
    });
  });

  resourceWithScheduleData = computed(() => {
    if (!this.isResource()) {
      return null;
    }
    return this.resourcesWithSchedulesForSelectedArea().find((r) => r.resourceId === this.resourceId()) ?? null;
  });

  protected readonly translation = translation;

  dialogRef = input<DialogRef>();
  router = inject(Router);

  navigatable = computed(() => {
    if (!this.navigationDetail.hasValue() || !this.navigationDetail.value() || !this.selectedArea()?.floorplan) {
      return false;
    }

    if (this.isResource()) {
      return true;
    }

    if (this.qrCodeGeo()) {
      return !this.qrCodeGeo().notNavigationPoint;
    }
    return false;
  });

  private createNavigationQueryParams(
    key: 'startResourceId' | 'startQrCodeId' | 'targetResourceId' | 'targetQrCodeId',
    value: number | string
  ) {
    const modeKey = key.startsWith('start') ? 'startSelectionMode' : 'targetSelectionMode';

    return {
      [key]: value,
      [modeKey]: StartTargetSelectionModeEnum.ScanQR,
    };
  }

  private getNavigationUrl(queryParams: Record<string, number | string>) {
    const url = new URL('/navigation', this.config.value.navigationAppUrl);

    Object.entries(queryParams).forEach(([key, value]) => {
      url.searchParams.set(key, String(value));
    });

    return url.toString();
  }

  selectAsStart() {
    this.dialogRef()?.close();
    if (this.isResource()) {
      const queryParams = this.createNavigationQueryParams('startResourceId', this.resourceId()!);
      if (this.isAnonymous()) {
        window.location = this.getNavigationUrl(queryParams) as any;
      } else {
        void this.router.navigate(['/navigation'], {
          queryParams,
        });
      }
    } else {
      const queryParams = this.createNavigationQueryParams('startQrCodeId', this.qrCodeId());
      if (this.isAnonymous()) {
        window.location = this.getNavigationUrl(queryParams) as any;
      } else {
        void this.router.navigate(['/navigation'], {
          queryParams,
        });
      }
    }
  }

  navigateTo() {
    this.dialogRef()?.close();
    if (this.isResource()) {
      const queryParams = this.createNavigationQueryParams('targetResourceId', this.resourceId()!);
      if (this.isAnonymous()) {
        window.location = this.getNavigationUrl(queryParams) as any;
      } else {
        void this.router.navigate(['/navigation'], {
          queryParams,
        });
      }
    } else {
      const queryParams = this.createNavigationQueryParams('targetQrCodeId', this.qrCodeId());
      if (this.isAnonymous()) {
        window.location = this.getNavigationUrl(queryParams) as any;
      } else {
        void this.router.navigate(['/navigation'], {
          queryParams,
        });
      }
    }
  }
}
